import React, { useEffect, useState } from 'react';
import { onToast } from '../utils/toastBus';
import { ALERT_STYLES } from '../styles/alertPalette';

const TOAST_ICONS = {
  success: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  )
};

export default function Toaster() {
  const [toasts, setToasts] = useState([]);
  const MAX_TOASTS = 3;

  useEffect(() => {
    const off = onToast((t) => {
      if (String(t?.type || '').toLowerCase() === 'error') {
        try {
          // eslint-disable-next-line no-console
          console.error('[toast:error]', t?.message || '');
        } catch {}
      }
      setToasts((prev) => {
        // Limit to MAX_TOASTS, remove oldest if needed
        const newToasts = [...prev, t];
        return newToasts.slice(-MAX_TOASTS);
      });
      
      const duration = Number.isFinite(Number(t.duration)) ? Number(t.duration) : 5000;
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.id !== t.id));
        }, duration);
      }
    });
    return () => off();
  }, []);

  const close = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));

  if (!toasts.length) return null;
  
  return (
    <div 
      role="region" 
      aria-label="Notifications"
      style={{ 
        position: 'fixed', 
        right: 16, 
        bottom: 16, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 12, 
        zIndex: 9999,
        maxWidth: 'calc(100vw - 32px)'
      }}
    >
      {toasts.map((t, index) => {
        const type = t.type || 'info';
        const style = ALERT_STYLES[type] || ALERT_STYLES.info;
        const icon = TOAST_ICONS[type] || TOAST_ICONS.info;
        
        return (
          <div 
            key={t.id}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={{
              minWidth: 300,
              maxWidth: 420,
              padding: '14px 16px',
              borderRadius: 14,
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              color: '#fff',
              ...style,
              animation: 'slideIn 0.3s ease-out',
              opacity: 1 - (index * 0.1),
              transform: `translateY(${index * -4}px)`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true">
                {icon}
              </div>
              <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
                {t.message}
              </div>
              <button 
                onClick={() => close(t.id)} 
                style={{ 
                  background: 'transparent', 
                  border: 0, 
                  color: '#fff', 
                  cursor: 'pointer', 
                  fontSize: 20, 
                  opacity: 0.8,
                  padding: 0,
                  lineHeight: 1,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.opacity = 1}
                onMouseLeave={(e) => e.target.style.opacity = 0.8}
                aria-label={`Close ${type} notification`}
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
